import type { components } from "@sagot/api-client";
import { useTranslation } from "@sagot/i18n";
import { ErrorState, Icon, Popover, PopoverContent, PopoverTrigger, Skeleton, StatCard } from "@sagot/ui";
import type { UseQueryResult } from "@tanstack/react-query";
import { useFormatters } from "../shared/format";
import { durationLabel, percentDelta, pointsDelta } from "./analytics-model";

type Summary = components["schemas"]["SummaryOut"];
const KPI_COUNT = 8;
const HOURS_DIGITS = 1;

function HoursSavedCard({ summary }: { summary: Summary }) {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const hours = summary.staff_hours_saved.toFixed(HOURS_DIGITS);
  return (
    <div className="card card-body">
      <div className="flex items-center justify-between">
        <span className="stat-label">{t("engage.analytics.kpi.hoursSaved")}</span>
        <Popover>
          <PopoverTrigger asChild>
            <button type="button" className="inline-flex size-6 items-center justify-center rounded-md text-fg-subtle hover:text-fg" aria-label={t("engage.analytics.kpi.howCalculated")}>
              <Icon name="info" className="size-4" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-64 text-xs leading-relaxed">
            <div className="font-bold">{t("engage.analytics.kpi.assumptionsTitle")}</div>
            <p className="mt-1 text-fg-muted">
              {t("engage.analytics.kpi.assumptionsBody", {
                resolved: fmt.number(summary.ai_resolved),
                minutes: summary.assumptions.minutes_per_conversation,
                hours,
                rate: fmt.peso(summary.assumptions.hourly_cost),
              })}
            </p>
            <p className="mt-1 text-fg-subtle">{t("engage.analytics.kpi.assumptionsEdit")}</p>
          </PopoverContent>
        </Popover>
      </div>
      <div className="stat-value">{t("engage.analytics.kpi.hours", { value: hours })}</div>
      <div className="mt-1 text-xs font-semibold text-success">{t("engage.analytics.kpi.staffValue", { value: fmt.peso(summary.staff_value) })}</div>
    </div>
  );
}

function KpiCards({ summary }: { summary: Summary }) {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const vsPrev = (value: string) => t("engage.analytics.kpi.vsPrevious", { value });
  const pts = (value: string) => t("engage.analytics.kpi.pts", { value });
  const perConversation = summary.messages_per_conversation;
  const csat = summary.csat_avg;
  return (
    <>
      <StatCard label={t("engage.analytics.kpi.conversations")} icon="messages-square" value={fmt.number(summary.conversations)} delta={percentDelta(summary.conversations_delta_pct, vsPrev)} />
      <StatCard label={t("engage.analytics.kpi.uniqueUsers")} icon="users" value={fmt.number(summary.unique_users)} footnote={t("engage.analytics.kpi.distinctVisitors")} />
      <StatCard
        label={t("engage.analytics.kpi.messages")}
        icon="message-square"
        value={fmt.number(summary.messages)}
        footnote={perConversation === null ? undefined : t("engage.analytics.kpi.perConversation", { value: perConversation.toFixed(1) })}
      />
      <StatCard label={t("engage.analytics.kpi.aiResolution")} icon="check-circle" value={fmt.percent(summary.ai_resolution_rate)} delta={pointsDelta(summary.ai_resolution_delta_pts, pts)} />
      <StatCard label={t("engage.analytics.kpi.handoffRate")} icon="headset" value={fmt.percent(summary.handoff_rate, 1)} delta={pointsDelta(summary.handoff_delta_pts, pts, false)} />
      <StatCard
        label={t("engage.analytics.kpi.avgResponse")}
        icon="zap"
        value={durationLabel(summary.avg_response_ms)}
        footnote={summary.p95_response_ms === null ? undefined : t("engage.analytics.kpi.p95", { value: durationLabel(summary.p95_response_ms) })}
      />
      <StatCard
        label={t("engage.analytics.kpi.csat")}
        icon="thumbs-up"
        value={
          csat === null ? (
            t("engage.analytics.kpi.noData")
          ) : (
            <>
              {csat.toFixed(1)}
              <span className="text-base font-bold text-fg-subtle">/5</span>
            </>
          )
        }
        footnote={
          summary.csat_ratings > 0
            ? t("engage.analytics.kpi.ratings", { count: summary.csat_ratings, positive: fmt.percent(summary.positive_pct) })
            : t("engage.analytics.kpi.noRatings", { positive: fmt.percent(summary.positive_pct) })
        }
      />
      <HoursSavedCard summary={summary} />
    </>
  );
}

/** Eight KPI cards (grid 2 / md:4) from `GET /v1/analytics/summary`. */
export function KpiGrid({ query }: { query: UseQueryResult<Summary> }) {
  const { t } = useTranslation();
  if (query.isError) {
    return (
      <div className="card">
        <ErrorState title={t("engage.analytics.loadError")} retryLabel={t("common.retry")} onRetry={() => void query.refetch()} />
      </div>
    );
  }
  return (
    <section className="grid grid-cols-2 gap-4 md:grid-cols-4" aria-label={t("engage.analytics.title")} aria-busy={query.isPending} data-testid="analytics-kpis">
      {query.data ? (
        <KpiCards summary={query.data} />
      ) : (
        Array.from({ length: KPI_COUNT }, (_, i) => (
          <div key={i} className="card card-body space-y-2" aria-hidden="true">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-7 w-20" />
            <Skeleton className="h-3 w-28" />
          </div>
        ))
      )}
    </section>
  );
}
